import React, { useEffect, useState, useRef } from "react";

/**
 * Lightweight typewriter that types `text` character-by-character on mount.
 * - Re-types whenever `text` changes (e.g. language switch)
 * - Respects `prefers-reduced-motion` and shows the full string immediately
 * - Shows a blinking caret while typing, fades it out at the end
 *
 * Props:
 *   text       string to render (required)
 *   speed      ms per character (default 38)
 *   startDelay ms before typing begins (default 180)
 *   caret      boolean — show blinking caret (default true)
 *   className  passthrough class for the outer span
 *   testId     optional data-testid
 */
export default function Typewriter({
  text = "",
  speed = 38,
  startDelay = 180,
  caret = true,
  className = "",
  testId = "typewriter",
}) {
  const [out, setOut] = useState("");
  const [done, setDone] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // Respect reduced motion — show full text immediately.
    const prefersReduced = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setOut(text);
      setDone(true);
      return undefined;
    }

    setOut("");
    setDone(false);
    let i = 0;
    const tick = () => {
      i += 1;
      setOut(text.slice(0, i));
      if (i >= text.length) {
        setDone(true);
        return;
      }
      timerRef.current = setTimeout(tick, speed);
    };
    timerRef.current = setTimeout(tick, startDelay);
    return () => clearTimeout(timerRef.current);
  }, [text, speed, startDelay]);

  return (
    <span data-testid={testId} className={className} aria-label={text}>
      <span>{out}</span>
      {caret && (
        <span
          aria-hidden
          className="inline-block ml-1 align-baseline"
          style={{
            width: "0.08em",
            height: "0.9em",
            background: "currentColor",
            transform: "translateY(0.1em)",
            opacity: done ? 0 : 1,
            transition: "opacity 600ms ease 400ms",
            animation: done ? "none" : "popoyoCaretBlink 1s steps(1) infinite",
          }}
        />
      )}
    </span>
  );
}
